/**
 * ChatPage
 *
 * Main chat interface page with URL-based session routing.
 */

import { useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChatWindow } from '../components/Chat';
import { useSessions, useAuth } from '../hooks';

export function ChatPage() {
  const { sessionUuid: urlSessionUuid } = useParams<{ sessionUuid?: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { sessions, currentSessionUuid, selectSession } = useSessions();
  const hasInitializedRef = useRef(false);

  // Sync URL with session selection
  useEffect(() => {
    if (!isAuthenticated || sessions.length === 0) return;

    // On initial load, if URL has sessionUuid, select that session
    if (urlSessionUuid && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      // Check if session exists in user's sessions
      const sessionExists = sessions.some(s => s.session_uuid === urlSessionUuid);
      if (sessionExists && urlSessionUuid !== currentSessionUuid) {
        selectSession(urlSessionUuid);
      } else if (!sessionExists) {
        // Session doesn't exist, redirect to home
        navigate('/', { replace: true });
      }
    }
  }, [isAuthenticated, sessions, urlSessionUuid, currentSessionUuid, selectSession, navigate]);

  // Update URL when session changes (but not on initial load)
  useEffect(() => {
    if (!isAuthenticated || !hasInitializedRef.current) return;

    if (currentSessionUuid && currentSessionUuid !== urlSessionUuid) {
      navigate(`/chat/${currentSessionUuid}`, { replace: true });
    } else if (!currentSessionUuid && urlSessionUuid) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, currentSessionUuid, urlSessionUuid, navigate]);

  return <ChatWindow />;
}

export default ChatPage;
